import {Reward} from './Reward.js';
import {QuestGoal} from './QuestGoal.js';
import {Emitter} from '@wonderlandengine/api';

import type {GameAnalytics as GA} from 'gameanalytics';
import {ProgressionStatus} from '../utils/game-analytics-enums.js';
import {globalAudioManager} from '@wonderlandengine/spatial-audio';
import {Sounds} from '../utils/Audio.js';
declare var GameAnalytics: typeof GA;

//
// Enum to maintain different types of quests for better scalability and management
export enum QuestType {
    Kill,
    Collect,
    Deliver,
}

/* All quests in the game */
export const AllQuests: Record<string, Quest> = {};
function registerQuest(quest: Quest) {
    AllQuests[quest.id] = quest;
}

/**
 * Quest interface that can be implemented for different types of quests
 */
export abstract class Quest {
    completed = false;

    id: string;
    description: string;
    abstract questType: QuestType;
    abstract goal: QuestGoal;
    abstract reward: Reward;
    requiredLevel = 1;

    abstract checkCompleted(): boolean;
    abstract giveReward(): Reward;
    abstract action(name: string): void;

    constructor(id: string, description: string, requiredLevel: number) {
        if (!id) throw new Error('Quest needs an id.');

        this.id = id;
        this.description = description;
        this.requiredLevel = requiredLevel;

        this.onRewarded.add(() => {
            GameAnalytics.addProgressionEvent(
                ProgressionStatus.Complete,
                'quest',
                this.id,
                'Level_Progress'
            );
        });
        this.onAssigned.add(() => {
            GameAnalytics.addProgressionEvent(
                ProgressionStatus.Start,
                'quest',
                this.id,
                'Level_Progress'
            );
        });
        this.onCompleted.once(() => {
            globalAudioManager.playOneShot(Sounds.Achievement, {volume: 0.5});
        });

        registerQuest(this);
    }

    /** Emitted when the conditions for the quest are completed */
    onCompleted = new Emitter();
    /** Emitted when the player accepts the quest */
    onAssigned = new Emitter();
    /** Emitted when the player hands in the quest for reward after completion */
    onRewarded = new Emitter();
}
